import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import zktecoService from '../services/zktecoService';
import './ZKTecoAgentManagement.css';

const ZKTecoAgentManagement = () => {
  const { hasRole, hasModulePermission } = useAuth();
  const canCreate = hasModulePermission('ZKTECO_AGENTS', 'create');
  const canDelete = hasModulePermission('ZKTECO_AGENTS', 'delete');
  const canUpdate = hasModulePermission('ZKTECO_AGENTS', 'update');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  
  // Formulaire d'enregistrement
  const [registerForm, setRegisterForm] = useState({
    name: '',
    version: '',
    ipAddress: '',
  });

  useEffect(() => {
    loadAgents();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadAgents, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const data = await zktecoService.getAllAgents();
      setAgents(data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des agents', err);
      setError('Erreur lors du chargement des agents');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAgent = async (e) => {
    e.preventDefault();
    try {
      const response = await zktecoService.registerAgent(registerForm);
      setShowRegisterForm(false);
      setRegisterForm({ name: '', version: '', ipAddress: '' });
      setSelectedAgent(response);
      setShowApiKeyDialog(true);
      loadAgents();
      alert('Agent enregistré avec succès ! Notez la clé API - elle ne sera plus affichée.');
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement de l\'agent', err);
      alert('Erreur lors de l\'enregistrement de l\'agent: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!canDelete) {
      alert('Vous n\'avez pas les permissions nécessaires pour supprimer un agent');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet agent ?')) {
      return;
    }

    try {
      await zktecoService.deleteAgent(agentId);
      loadAgents();
      alert('Agent supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'agent', err);
      alert('Erreur lors de la suppression de l\'agent: ' + (err.response?.data?.error || err.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ONLINE':
        return 'status-online';
      case 'OFFLINE':
        return 'status-offline';
      case 'ERROR':
        return 'status-error';
      default:
        return 'status-unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ONLINE':
        return 'En ligne';
      case 'OFFLINE':
        return 'Hors ligne';
      case 'ERROR':
        return 'Erreur';
      default:
        return 'Inconnu';
    }
  };

  if (loading && agents.length === 0) {
    return (
      <div className="zkteco-management">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="zkteco-management">
      <div className="management-header">
        <h1>Gestion des Agents ZKTeco</h1>
        {canCreate && (
          <button
            className="btn btn-primary"
            onClick={() => setShowRegisterForm(true)}
          >
            Enregistrer un nouvel agent
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {showRegisterForm && (
        <div className="modal-overlay" onClick={() => setShowRegisterForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Enregistrer un nouvel agent</h2>
            <form onSubmit={handleRegisterAgent}>
              <div className="form-group">
                <label>Nom de l'agent *</label>
                <input
                  type="text"
                  required
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  placeholder="Ex: Bureau Principal"
                />
              </div>
              <div className="form-group">
                <label>Version</label>
                <input
                  type="text"
                  value={registerForm.version}
                  onChange={(e) => setRegisterForm({ ...registerForm, version: e.target.value })}
                  placeholder="Ex: 1.0.0"
                />
              </div>
              <div className="form-group">
                <label>Adresse IP</label>
                <input
                  type="text"
                  value={registerForm.ipAddress}
                  onChange={(e) => setRegisterForm({ ...registerForm, ipAddress: e.target.value })}
                  placeholder="Ex: 192.168.1.100"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Enregistrer
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRegisterForm(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showApiKeyDialog && selectedAgent && (
        <div className="modal-overlay" onClick={() => setShowApiKeyDialog(false)}>
          <div className="modal-content api-key-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Agent enregistré avec succès</h2>
            <div className="api-key-info">
              <p><strong>Agent ID:</strong> {selectedAgent.agentId}</p>
              <p><strong>Clé API:</strong></p>
              <div className="api-key-display">
                <code>{selectedAgent.apiKey}</code>
                <button
                  className="btn btn-small"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedAgent.apiKey);
                    alert('Clé API copiée dans le presse-papiers');
                  }}
                >
                  Copier
                </button>
              </div>
              <p className="warning">⚠️ Notez cette clé API - elle ne sera plus affichée après fermeture</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowApiKeyDialog(false);
                setSelectedAgent(null);
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      <div className="agents-list">
        {agents.length === 0 ? (
          <div className="empty-state">
            <p>Aucun agent enregistré</p>
          </div>
        ) : (
          <table className="agents-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>ID Agent</th>
                <th>Statut</th>
                <th>Dernier Contact</th>
                <th>Version</th>
                <th>Adresse IP</th>
                <th>Date de création</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.id}>
                  <td>{agent.name}</td>
                  <td>
                    <code className="agent-id">{agent.agentId}</code>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusColor(agent.status)}`}>
                      {getStatusLabel(agent.status)}
                    </span>
                  </td>
                  <td>{formatDate(agent.lastHeartbeat)}</td>
                  <td>{agent.version || '-'}</td>
                  <td>{agent.ipAddress || '-'}</td>
                  <td>{formatDate(agent.createdAt)}</td>
                  <td className="actions-column">
                    {canDelete && (
                      <button
                        className="btn btn-danger btn-small"
                        onClick={() => handleDeleteAgent(agent.agentId)}
                        title="Supprimer"
                      >
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ZKTecoAgentManagement;

